import { useMemo, useState } from "react"
import { ActionIcon, AppShell, Box, Burger, Flex, Group, Image, Switch, Title, Tooltip, useComputedColorScheme, useMantineColorScheme } from "@mantine/core"
import classes from "./DashboardLayout.module.scss"
import { Outlet, useLocation } from "react-router-dom"
import { useDisclosure, useLocalStorage, useMediaQuery } from "@mantine/hooks"
import { LOCAL_PREFERENCES_PREFIX, ROLES } from "@shared/constants"
import UserButton from "@shared/components/UserButton/UserButton.tsx"
import biocellsLogoWhite from "/logo-white.svg"
import biocellsLogoDark from "/logo-black.svg"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { useMsal } from "@azure/msal-react"
import { useLogout } from "@/features/auth/hooks/auth.queries"
import { IconLogout, IconMoonStars, IconReload, IconSun } from "@tabler/icons-react"
import { MsalAuthService } from "@/features/auth/services/auth.service"
import { ILink, mainLinks } from "../utils/menu"
import { useGetMisAccesosRendicion, useGetPendientesRevision } from "@/features/rendiciones/rendicion/hooks/rendicion-queries"
import NotificacionesBell from "@/features/rendiciones/rendicion/components/NotificacionesBell"
import { useHeaderState } from "@shared/state/header.state.ts"
import { DoubleNavbar } from "@shared/components/DoubleNavbar/DoubleNavbar.tsx"
import { ImpersonationBanner } from "@shared/components/ImpersonationBanner/ImpersonationBanner"

export default function DashboardLayout() {
  const [bannerHeight, setBannerHeight] = useState(0)
  const location = useLocation()
  const { title: customTitle, onRefresh } = useHeaderState()
  const [mobileOpened, { close: closeMobile, toggle: toggleMobile }] = useDisclosure()
  const [desktopOpened, setDesktopOpened] = useLocalStorage({ key: `${LOCAL_PREFERENCES_PREFIX}navbar-desktop-opened`, defaultValue: true, getInitialValueInEffect: false })
  const toggleDesktop = () => setDesktopOpened((opened) => !opened)
  const openDesktop = () => setDesktopOpened(true)
  const matchesXSQuery = useMediaQuery("(min-width: 36em)")
  const computedColorScheme = useComputedColorScheme("light", { getInitialValueInEffect: true })
  const { user, hasRole, logout } = useAuth()
  const { setColorScheme } = useMantineColorScheme({ keepTransitions: true })
  const { mutateAsync: logoutMutationAsync, isPending: isLogoutAsyncPending } = useLogout()
  const { instance } = useMsal()

  const puedeRevisarRendiciones = hasRole(ROLES.RENDICION_APPROVE_PE) || hasRole(ROLES.RENDICION_APPROVE_EC) || hasRole(ROLES.RENDICION_EDIT)
  const { data: pendientesRendicion } = useGetPendientesRevision(puedeRevisarRendiciones)
  const { data: accesosRendicion } = useGetMisAccesosRendicion()
  const sideLinks = useMemo(() => mainLinks({ hasRole, pendientesRendicion, accesosRendicion, paisUsuario: user?.country }), [hasRole, pendientesRendicion, accesosRendicion, user?.country])

  const pageTitle = useMemo(() => {
    const activeLabels: string[] = []

    const findActive = (items: Array<Omit<ILink, "icon">>): boolean => {
      for (const item of items) {
        if (!item.visible) continue

        const isSelfActive = !!(item.to && (location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(`${item.to}/`))))
        const hasActiveChild = !!(item.children && findActive(item.children))

        if (isSelfActive || hasActiveChild) {
          activeLabels.push(item.label)
          return true
        }
      }
      return false
    }

    findActive(sideLinks)
    return activeLabels.length > 0 ? activeLabels.reverse().join(" - ") : "Biocells Discoveries"
  }, [location.pathname, sideLinks])

  return (
    <AppShell header={{ height: 60 + bannerHeight }} padding="md" navbar={{ width: desktopOpened ? 270 : 60, breakpoint: "sm", collapsed: { mobile: !mobileOpened, desktop: false } }}>
      <AppShell.Header>
        <ImpersonationBanner onHeightChange={setBannerHeight} />
        <Group gap={matchesXSQuery ? "lg" : "xs"} px="md" align="center" justify="space-between" h={60} wrap="nowrap" style={{ overflow: "hidden" }}>
          <Flex gap="xs" align="center">
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <Burger opened={desktopOpened} onClick={toggleDesktop} visibleFrom="sm" size="sm" />
            <Box h={35} w={100}>
              <Image h="100%" src={computedColorScheme === "light" ? biocellsLogoDark : biocellsLogoWhite} alt="Logo Biocells" />
            </Box>
          </Flex>

          <Flex align="center" gap="xs">
            <Title className={classes.title} order={2}>
              {customTitle || pageTitle}
            </Title>
            <Tooltip label="Refrescar">
              <ActionIcon
                color={computedColorScheme === "light" ? "biocells-green.5" : "biocells-green.7"}
                onClick={() => {
                  if (onRefresh) {
                    onRefresh()
                  } else {
                    window.location.reload()
                  }
                }}
                size="lg"
              >
                <IconReload size="1.2rem" stroke={2} />
              </ActionIcon>
            </Tooltip>
          </Flex>

          <Group justify="space-between">
            <Flex align="center" gap="lg">
              <NotificacionesBell />
              <Group visibleFrom="xs">
                <UserButton withLogout={false} />
              </Group>
              <Box className={classes.optionsContainer}>
                <Tooltip label={`Cambiar a modo ${computedColorScheme === "light" ? "dark" : "light"}`}>
                  <Group justify="center" className={classes.switchContainer}>
                    <Switch classNames={{ track: classes.track, root: classes.switchRoot }} onChange={() => setColorScheme(computedColorScheme === "light" ? "dark" : "light")} size="sm" onLabel={<IconSun size="1rem" stroke={2.5} />} offLabel={<IconMoonStars size="1rem" stroke={2.5} />} />
                  </Group>
                </Tooltip>
                <Tooltip label="Cerrar Sesión">
                  <ActionIcon
                    color="red"
                    onClick={async () => {
                      const isLocalLogout = !user?.tenantId || hasRole("provider")
                      await MsalAuthService.handleLogout({ logout, logoutMutationAsync, instance, isLocalLogout })
                    }}
                    loading={isLogoutAsyncPending}
                  >
                    <IconLogout className={classes.iconLogout} />
                  </ActionIcon>
                </Tooltip>
              </Box>
            </Flex>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar>
        <DoubleNavbar links={sideLinks} onLinkClick={closeMobile} opened={desktopOpened} onOpen={openDesktop} />
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  )
}
